import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Button, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { setMainHeading } from '../../../redux/modules/mainHeading/slice';
import { getExternalReferrersRequest, resetCreateExternalReferrer, resetUpdateExternalReferrer, resetDeleteExternalReferrer } from '../../../redux/modules/externalReferrer/slice';
import ExternalReferrerList from './list';
import ExternalReferrerForm from './form';

const ExternalReferrers = () => {
    const dispatch = useDispatch();
    const { getExternalReferrersRes, createExternalReferrerRes, updateExternalReferrerRes, deleteExternalReferrerRes, loading } = useSelector(state => state.externalReferrer);

    const [page, setPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [formOpen, setFormOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        dispatch(setMainHeading("External Access"));
    }, [dispatch]);

    useEffect(() => {
        fetchExternalReferrers();
    }, [page, searchQuery]);

    useEffect(() => {
        if (createExternalReferrerRes?.success) {
            setSnackbar({ open: true, message: 'External referrer created successfully', severity: 'success' });
            setFormOpen(false);
            fetchExternalReferrers();
            dispatch(resetCreateExternalReferrer());
        } else if (createExternalReferrerRes && !createExternalReferrerRes.success) {
            setSnackbar({ open: true, message: createExternalReferrerRes.message || 'Error creating external user', severity: 'error' });
            dispatch(resetCreateExternalReferrer());
        }
    }, [createExternalReferrerRes]);

    useEffect(() => {
        if (updateExternalReferrerRes?.success) {
            setSnackbar({ open: true, message: 'External referrer updated successfully', severity: 'success' });
            setFormOpen(false);
            setEditData(null);
            fetchExternalReferrers();
            dispatch(resetUpdateExternalReferrer());
        } else if (updateExternalReferrerRes && !updateExternalReferrerRes.success) {
            setSnackbar({ open: true, message: updateExternalReferrerRes.message || 'Error updating external user', severity: 'error' });
            dispatch(resetUpdateExternalReferrer());
        }
    }, [updateExternalReferrerRes]);

    useEffect(() => {
        if (deleteExternalReferrerRes?.success) {
            setSnackbar({ open: true, message: 'External referrer deleted successfully', severity: 'success' });
            fetchExternalReferrers();
            dispatch(resetDeleteExternalReferrer());
        } else if (deleteExternalReferrerRes && !deleteExternalReferrerRes.success) {
            setSnackbar({ open: true, message: deleteExternalReferrerRes.message || 'Error deleting external user', severity: 'error' });
            dispatch(resetDeleteExternalReferrer());
        }
    }, [deleteExternalReferrerRes]);

    const fetchExternalReferrers = () => {
        dispatch(getExternalReferrersRequest({ page, limit: 10, searchQuery }));
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        setPage(1);
    };

    const handleEdit = (referrer) => {
        setEditData(referrer);
        setFormOpen(true);
    };

    const handleCloseForm = () => {
        setFormOpen(false);
        setEditData(null);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">
                    External Access
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setFormOpen(true)}
                >
                    Add External User
                </Button>
            </Box>

            <Box sx={{ mb: 3 }}>
                <TextField
                    placeholder="Search by name, email, or organization..."
                    value={searchQuery}
                    onChange={handleSearch}
                    size="small"
                    sx={{ width: 400 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <ExternalReferrerList
                data={getExternalReferrersRes?.data || []}
                totalCount={getExternalReferrersRes?.dataCount || 0}
                page={page}
                onPageChange={setPage}
                onEdit={handleEdit}
                loading={loading}
            />

            <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editData ? 'Edit External User' : 'Add External User'}
                </DialogTitle>
                <DialogContent>
                    <ExternalReferrerForm
                        editData={editData}
                        onClose={handleCloseForm}
                    />
                </DialogContent>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ExternalReferrers;
